import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Tag, Boxes } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fileUrl, money } from "@/lib/api";
import { COLORS } from "@/lib/colors";
import { Card, PageHeader, StatusBadge, EmptyState, Btn, inputCls } from "@/components/admin/ui";
import InventoryDialog from "@/components/admin/InventoryDialog";

export default function Products() {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [inv, setInv] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/admin/products").then((r) => setProducts(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (p) => {
    const status = p.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/admin/products/${p.id}/status`, { status });
      toast.success(status === "published" ? "Product is now live" : "Product hidden from store");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const list = (products || []).filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-products-page">
      <PageHeader title="Products" subtitle="Add products, update prices and manage stock.">
        <Link to="/admin/products/new"><Btn data-testid="products-add-button"><Plus size={16} /> Add product</Btn></Link>
      </PageHeader>
      <Card className="p-0 sm:p-0 overflow-hidden">
        {products && products.length === 0 ? (
          <EmptyState icon={Tag} title="Add your first product" text="Upload your photos, set colors and stock, and publish when you're ready." testid="products-empty">
            <Link to="/admin/products/new"><Btn data-testid="products-empty-add-button"><Plus size={16} /> Add product</Btn></Link>
          </EmptyState>
        ) : (
          <>
            <div className="p-3 border-b border-zinc-200">
              <div className="relative max-w-sm">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className={`${inputCls} pl-9`} data-testid="products-search-input" />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="products-table">
                <thead className="bg-zinc-50 text-zinc-500 text-xs">
                  <tr>
                    <th className="text-left font-medium px-4 py-2.5">Product</th>
                    <th className="text-left font-medium px-4 py-2.5">Status</th>
                    <th className="text-left font-medium px-4 py-2.5">Stock</th>
                    <th className="text-left font-medium px-4 py-2.5">Category</th>
                    <th className="text-right font-medium px-4 py-2.5">Price</th>
                    <th className="text-right font-medium px-4 py-2.5">Live</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {list.map((p) => (
                    <tr key={p.id} className="hover:bg-zinc-50" data-testid={`product-row-${p.slug}`}>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => nav(`/admin/products/${p.id}`)}>
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-md bg-zinc-100 border border-zinc-200 overflow-hidden flex-shrink-0">
                            {(p.cover_photo || p.photos[0]) && <img src={fileUrl(p.cover_photo || p.photos[0])} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <span className="font-medium text-zinc-950 hover:underline">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3">
                        <button onClick={() => setInv(p)} className="flex items-center gap-2 hover:bg-zinc-100 rounded-md px-2 py-1 -mx-2" data-testid={`product-inventory-button-${p.slug}`}>
                          <span className="flex -space-x-1">{p.colors.slice(0, 5).map((c) => <span key={c.name} className="w-3.5 h-3.5 rounded-full border border-white ring-1 ring-zinc-200" style={{ background: COLORS[c.name] }} />)}</span>
                          <span className={p.total_stock === 0 ? "text-red-600" : "text-zinc-700"}>{p.total_stock} in stock</span>
                          <Boxes size={14} className="text-zinc-400" />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-zinc-600">{p.category || "—"}</td>
                      <td className="px-4 py-3 text-right">{money(p.price)}</td>
                      <td className="px-4 py-3 text-right"><Switch checked={p.status === "published"} onCheckedChange={() => toggle(p)} data-testid={`product-publish-toggle-${p.slug}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
      <InventoryDialog product={inv} onClose={() => setInv(null)} onSaved={load} />
    </div>
  );
}
